
import { DeleteRounded as DeleteRoundedIcon } from '@mui/icons-material'
import { Button, Chip, Container, Stack, Typography } from '@mui/material'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { useGlobalContext } from '../../Shared/Contexts/GlobalContext.jsx'
import ConfirmWithCheckDialog from '../../Shared/Dialogs/ConfirmWithCheckDialog.jsx'
import FromCityeyeFilter from '../../Shared/Filters/Fields/FromCityeyeFilter.jsx'
import { useGateways } from '../Contexts/GatewaysContext.jsx'
import AddGateway from '../Dialogs/AddGateway.jsx'
import GatewayDetail from '../Dialogs/GatewayDetail.jsx'
import UpdateGateway from '../Dialogs/UpdateGateway.jsx'
import GatewaysFilters from '../Filters/Filters.jsx'
import GatewaysTable from '../Tables/GatewaysTable.jsx'

const DEFAULT_PAGE_SIZE = 25

const Gateways = () => {
  const [searchParams] = useSearchParams()
  const { useSearchGateways, useFetchGateway, useCreateGateway, useUpdateGateway, useDeleteGateway } = useGateways()
  const { openErrorAlert, openSuccessAlert } = useGlobalContext()
  const fullText = searchParams.get('search')

  const [filters, setFilters] = useState({})

  const [gateways, setGateways] = useState({
    data: [],
    count: 0
  })

  const { entityId } = useParams()

  const [isGatewaysLoading, setIsGatewaysLoading] = useState(false)
  const [isGatewaysDetailDialogOpen, setIsGatewaysDetailDialogOpen] = useState(false)
  const [isAddGatewayDialogOpen, setIsAddGatewayDialogOpen] = useState(false)
  const [isUpdateGatewayDialogOpen, setIsUpdateGatewayDialogOpen] = useState(false)
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false)
  const [selectedGateway, setSelectedGateway] = useState(null)
  const [selectedSerial, setSelectedSerial] = useState(null)
  const [serialToDelete, setSerialToDelete] = useState(null)


  const [{ sort, direction }, setSorting] = useState({
    direction: 'desc',
    sort: 'updatedAt'
  })

  const [page, setPage] = useState(1)

  const handleFiltersChange = (filters) => setFilters(filters)

  const handleFromCityeyeFilterChange = (value) => setFilters({
    ...filters,
    ...value
  })

  useEffect(() => {
    if (!fullText || !fullText.length) {
      return
    }
    setIsGatewaysLoading(true)
    useSearchGateways({
      filters: {
        ...filters,
        fullText
      },
      sort,
      direction,
      page,
      pageSize: DEFAULT_PAGE_SIZE
    })
      .then((res) => setGateways({
        data: res.data,
        count: res.count
      }))
      .finally(() => setIsGatewaysLoading(false))
    setPage(1)
  }, [fullText, filters])

  const searchGateways = async () => {
    useSearchGateways({
      filters: {
        ...filters,
        fullText
      },
      sort,
      direction,
      page,
      pageSize: DEFAULT_PAGE_SIZE
    })
      .then((res) => setGateways({
        data: res.data,
        count: res.count
      }))
      .finally(() => setIsGatewaysLoading(false))
  }

  useEffect(() => {
    setIsGatewaysLoading(true)
    searchGateways()
  }, [filters, page, sort, direction])

  useEffect(() => {
    if (!entityId) {
      return
    }

    useFetchGateway(entityId)
      .then(({ data }) => {
        setSelectedGateway(data)
        setIsGatewaysDetailDialogOpen(true)
      })
      .catch(() => (err) => openErrorAlert({ keys: err.data.keys }))
  }, [entityId])


  const handleChangePage = (_, value) => setPage(value)
  const handleChangeSorting = (value) => setSorting(value)

  const handleCloseGatewayDetail = () => {
    setIsGatewaysDetailDialogOpen(false)
    setSelectedGateway(null)
  }

  const handleDetailEdit = () => {
    const serial = selectedGateway?.serial
    setIsGatewaysDetailDialogOpen(false)
    setSelectedGateway(null)
    handleOpenUpdateDialog(serial)
  }

  const handleDetailDelete = () => {
    handleDelete(selectedGateway?.serial)
  }

  const handleOpenAddDialog = () => {
    setIsAddGatewayDialogOpen(true)
  }

  const handleCloseAddDialog = () => {
    setIsAddGatewayDialogOpen(false)
  }

  const handleOpenUpdateDialog = (serial) => {
    setSelectedSerial(serial)
    setIsUpdateGatewayDialogOpen(true)
  }

  const handleCloseUpdateDialog = () => {
    setIsUpdateGatewayDialogOpen(false)
    setSelectedSerial(null)
  }

  const handleCreate = async (payload) => {
    useCreateGateway(payload)
      .then(() => {
        openSuccessAlert()
        setIsAddGatewayDialogOpen(false)
        searchGateways()
      })
      .catch((err) => openErrorAlert({ keys: err.data.keys }))
  }

  const handleUpdate = async (serial, payload) => {
    useUpdateGateway(serial, payload)
      .then(() => {
        openSuccessAlert()
        setIsUpdateGatewayDialogOpen(false)
        setSelectedSerial(null)
        searchGateways()
      })
      .catch((err) => openErrorAlert({ keys: err.data.keys }))
  }

  const handleDelete = (serial) => {
    setSerialToDelete(serial)
    setIsConfirmDeleteOpen(true)
  }

  const handleConfirmDelete = () => {
    useDeleteGateway(serialToDelete)
      .then(() => {
        openSuccessAlert()
        setIsConfirmDeleteOpen(false)
        setSerialToDelete(null)
        handleCloseGatewayDetail()
        searchGateways()
      })
      .catch((err) => openErrorAlert({ keys: err.data.keys }))
  }

  const handleCloseConfirmDelete = () => {
    setIsConfirmDeleteOpen(false)
    setSerialToDelete(null)
  }

  return (
    <Container sx={{ py: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}>
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={2}>
          <Typography variant="h2">{t('common.gateways')}</Typography>
          <Chip
            label={gateways?.count}
            color="primary"
          />
        </Stack>
        <Button
          id="add-gateway"
          variant="contained"
          onClick={handleOpenAddDialog}
        >
          {t('gateways.addGateway')}
        </Button>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        my={4}>
        <FromCityeyeFilter
          filters={filters}
          onChange={handleFromCityeyeFilterChange}
        />
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        my={4}>
        <GatewaysFilters
          filters={filters}
          onChange={handleFiltersChange}
          itemsCount={gateways?.count}
        />
      </Stack>

      <GatewaysTable
        gateways={gateways}
        page={page}
        sorting={{
          sort,
          direction
        }}
        onPageChange={handleChangePage}
        onSortingChange={handleChangeSorting}
        isLoading={isGatewaysLoading}
        onEdit={handleOpenUpdateDialog}
        onDelete={handleDelete}
      />

      <GatewayDetail
        isOpen={isGatewaysDetailDialogOpen}
        onClose={handleCloseGatewayDetail}
        selectedGateway={selectedGateway}
        onEdit={handleDetailEdit}
        onDelete={handleDetailDelete}
      />

      <AddGateway
        open={isAddGatewayDialogOpen}
        onClose={handleCloseAddDialog}
        onCreate={handleCreate}
      />


      {isUpdateGatewayDialogOpen && selectedSerial && (
        <UpdateGateway
          open={isUpdateGatewayDialogOpen}
          onClose={handleCloseUpdateDialog}
          serial={selectedSerial}
          onUpdate={handleUpdate}
        />
      )}

      <ConfirmWithCheckDialog
        isOpen={isConfirmDeleteOpen}
        onClose={handleCloseConfirmDelete}
        onConfirmAction={handleConfirmDelete}
        icon={<DeleteRoundedIcon sx={{ fontSize: 60, color: '#ef5350' }} />}
        questionText={t('gateways.deleteGateway.title')}
        subQuestionText={serialToDelete}
        copyText={serialToDelete}
        bodyText={t('gateways.deleteGateway.subtitle')}
        inputSuggestionText={t('gateways.deleteGateway.helper')}
      />

    </Container>
  )
}

export default Gateways
